import {
  releaseExportModalFocusTrap,
  removeTrapFocus,
  trapExportModalFocus,
  trapFocus,
} from '@theme/focus';

const CART_ITEMS_REMOVED_STORAGE_KEY = 'cart-items-removed-count';
// Only honor the "items removed" flag when it was set just before this cart load. Prevents a stale
// flag (e.g. a removal whose navigation never reached /cart) from surfacing a spurious
// "N product(s) removed" banner on a later, unrelated cart visit — such as after adding a list to cart.
const CART_ITEMS_REMOVED_FRESHNESS_MS = 10000;

class CartPageActions extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this._exportModalTrigger = null;
    this.init();

    this.todaysDate = new Date();
  }

  init() {
    this.initCartPageActionsApgMenu();
    this.setupHideImagesToggle();
    this.setupClearCartModal();
    this.showPrevDateBanner();
    this.initShowPrevDateProducts();
    this.setupCartExport();
    this.setupItemsRemovedBanner();
    this.showItemsRemovedBannerFromStorage();
  }

  /**
   * Wires the “More actions” `rs-actions` (when `show_more_actions` is on) to the same
   * menubutton + menu APG as the row-count control (arrows, Tab, Esc, roving menuitem, chevron).
   */
  initCartPageActionsApgMenu() {
    const run = () => {
      if (typeof window.wireCartPageActionsRsMenu === 'function') {
        window.wireCartPageActionsRsMenu();
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, {once: true});
    } else {
      run();
    }
  }

  setupClearCartModal() {
    const clearCartModal = document.getElementById('clearCartModal');
    if (!clearCartModal) return;

    const modalContent = clearCartModal.querySelector('.cart-page-actions__modal-content');
    const cancelClearCart = clearCartModal.querySelector('.rs-button-secondary');
    /** @type {HTMLElement | null} */
    let clearCartModalTrigger = null;

    const handleModalKeydown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeClearCartModal();
    };

    const openClearCartModal = (trigger) => {
      clearCartModalTrigger = trigger instanceof HTMLElement ? trigger : null;
      clearCartModal.classList.add('open');
      clearCartModal.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', handleModalKeydown);

      if (!(modalContent instanceof HTMLElement)) return;
      requestAnimationFrame(() => {
        if (!clearCartModal.classList.contains('open')) return;
        trapFocus(modalContent, {
          initialFocus: cancelClearCart instanceof HTMLElement ? cancelClearCart : null,
          inert: true,
        });
      });
    };

    const closeClearCartModal = () => {
      removeTrapFocus();
      document.removeEventListener('keydown', handleModalKeydown);
      clearCartModal.classList.remove('open');
      clearCartModal.setAttribute('aria-hidden', 'true');
      clearCartModalTrigger?.focus({preventScroll: true});
      clearCartModalTrigger = null;
    };

    const clearCartModalTriggers = document.querySelectorAll('.cart-page-actions__clear-cart');
    clearCartModalTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        openClearCartModal(trigger);
      });
    });

    const closeCartModal = clearCartModal.querySelector('.cart-page-actions__modal-close');
    if (closeCartModal) {
      closeCartModal.addEventListener('click', closeClearCartModal);
    }

    if (cancelClearCart) {
      cancelClearCart.addEventListener('click', closeClearCartModal);
    }

    const clearCart = clearCartModal.querySelector('.rs-button-primary');
    if (clearCart) {
      clearCart.addEventListener('click', () => {
        const cartItemCount = document.querySelectorAll('.cart-table__body .rs-table-body__row').length;
        if (cartItemCount > 0) {
          CartPageActions.setRemovedItemsCount(cartItemCount);
        }

        CartPageActions.clearCartAndAttributes()
          .then(() => {
            location.reload();
          })
          .catch((error) => {
            console.error('Error clearing cart:', error);
          });
      });
    }
  }

  setupHideImagesToggle() {
    const hideImagesToggles = this.querySelectorAll('.cart-page-actions__hide-images');
    if (hideImagesToggles.length === 0) return;

    // Setup each toggle button
    hideImagesToggles.forEach((hideImagesToggle) => {
      // Add initial state class
      if (hideImagesToggle instanceof HTMLElement) {
        this.updateToggleState(hideImagesToggle);
      }

      // Add event listener for change events (checkboxes)
      hideImagesToggle.addEventListener('change', (e) => {
        const target = e.target;
        if (target && 'checked' in target && target instanceof HTMLElement) {
          const isChecked = !!target.checked;
          this.toggleProductImages(isChecked);
          this.syncAllHideImagesToggles(isChecked);
        }
      });

      // Also handle click events for button elements
      if (hideImagesToggle.tagName === 'BUTTON') {
        hideImagesToggle.addEventListener('click', (e) => {
          e.preventDefault();
          const isChecked = hideImagesToggle.classList.contains('active') || hideImagesToggle.getAttribute('aria-pressed') === 'true';
          const newState = !isChecked;

          hideImagesToggle.classList.toggle('active', newState);
          hideImagesToggle.setAttribute('aria-pressed', newState.toString());

          this.toggleProductImages(newState);
          this.syncAllHideImagesToggles(newState);
        });
      }
    });
  }

  /**
   * Updates the toggle state and visual appearance
   * @param {HTMLElement} toggleElement - The toggle element to update
   */
  updateToggleState(toggleElement) {
    const isActive = ('checked' in toggleElement && toggleElement.checked) || toggleElement.classList.contains('active') || toggleElement.getAttribute('aria-pressed') === 'true';

    // Add class to control icon visibility
    toggleElement.classList.toggle('images-hidden', !!isActive);

    // Update aria attributes for accessibility
    toggleElement.setAttribute('aria-pressed', isActive.toString());
  }

  /**
   * Synchronizes all hide-images toggle buttons to match the given state
   * @param {boolean} isActive - The state to sync all toggles to
   */
  syncAllHideImagesToggles(isActive) {
    const allHideImagesToggles = document.querySelectorAll('.cart-page-actions__hide-images');

    allHideImagesToggles.forEach((toggle) => {
      if (toggle instanceof HTMLElement) {
        // Update checkbox state
        if ('checked' in toggle) {
          toggle.checked = isActive;
        }

        // Update button state
        toggle.classList.toggle('active', isActive);
        toggle.setAttribute('aria-pressed', isActive.toString());

        // Update visual state
        this.updateToggleState(toggle);
      }
    });
  }

  /**
   * Toggles the visibility of product images in the cart table
   * @param {boolean} hideImages - Whether to hide the images
   */
  toggleProductImages(hideImages) {
    const cartTable = document.querySelector('.cart-table');
    if (!cartTable) return;

    const imageCells = cartTable.querySelectorAll('.rs-table-body__cell--media');
    const imageHeader = cartTable.querySelector('thead th:nth-child(2)');

    if (hideImages) {
      // Hide images
      imageCells.forEach((cell) => cell.classList.add('hidden'));
      if (imageHeader) imageHeader.classList.add('hidden');
      cartTable.classList.add('images-hidden');
    } else {
      // Show images
      imageCells.forEach((cell) => cell.classList.remove('hidden'));
      if (imageHeader) imageHeader.classList.remove('hidden');
      cartTable.classList.remove('images-hidden');
    }

    // Store state in localStorage for persistence
    localStorage.setItem('cart-hide-images', hideImages.toString());
  }

  // Restore state from localStorage on page load
  restoreImageVisibilityState() {
    const hideImages = localStorage.getItem('cart-hide-images') === 'true';
    if (hideImages) {
      this.toggleProductImages(true);
      this.syncAllHideImagesToggles(true);
    }
  }

  /** @param {string | undefined} dateStr */
  isDateOutdated(dateStr) {
    if (!dateStr) return false;
    const requested = new Date(dateStr);
    if (isNaN(requested.getTime())) return false;
    requested.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return requested < today;
  }

  showPrevDateBanner() {
    const prevDateBanner = document.querySelector('.cart-page-actions__prev-date-banner');

    const cartItems = document.querySelectorAll('.cart-table__body .rs-table-body__row');
    cartItems.forEach((item) => {
      const input = /** @type {HTMLInputElement|null} */ (item?.querySelector('.cart-table__requested-delivery-date input'));
      const requestedDate = input?.value;

      if (this.isDateOutdated(requestedDate)) {
        prevDateBanner?.classList.add('rs-banner--visible');
        item.classList.add('prev-date-product');
      }

      this.trackDateChanges(input);
    });
  }

  initShowPrevDateProducts() {
    const prevDateBannerToggle = document.querySelector('.cart-page-actions__prev-date-banner-toggle');
    if (prevDateBannerToggle) {
      prevDateBannerToggle.addEventListener('click', () => {
        prevDateBannerToggle.classList.toggle('active');

        if (prevDateBannerToggle.classList.contains('active')) {
          this.showPrevDateProducts();
        } else {
          this.hidePrevDateProducts();
        }
      });
    }
  }

  showPrevDateProducts() {
    const cartTableBody = document.querySelector('.cart-table__body');
    cartTableBody?.classList.add('prev-date-products--only');
  }

  hidePrevDateProducts() {
    const cartTableBody = document.querySelector('.cart-table__body');
    cartTableBody?.classList.remove('prev-date-products--only');
  }

  trackDateChanges(input) {
    input?.addEventListener('change', () => {
      const requestedDate = input?.value;
      if (this.isDateOutdated(requestedDate)) {
        input.closest('.rs-table-body__row').classList.add('prev-date-product');
        this.showPrevDateBanner();
      } else {
        input.closest('.rs-table-body__row').classList.remove('prev-date-product');
        const hasOutdatedDates = document.querySelectorAll('.rs-table-body__row.prev-date-product').length > 0;
        if (!hasOutdatedDates) {
          document.querySelector('.cart-page-actions__prev-date-banner')?.classList.remove('rs-banner--visible');
        }
      }
    });
  }

  setupItemsRemovedBanner() {
    const banner = document.querySelector('.cart-page-actions__items-removed-banner');
    const closeButton = banner?.querySelector('.cart-page-actions__items-removed-banner-close');
    if (!banner) return;

    closeButton?.addEventListener('click', () => {
      this.hideItemsRemovedBanner();
    });
  }

  /**
   * @param {number} count
   */
  showItemsRemovedBanner(count) {
    const banner = document.querySelector('.cart-page-actions__items-removed-banner');
    const messageEl = banner?.querySelector('.cart-page-actions__items-removed-banner-message');
    if (!banner || !messageEl || !Number.isFinite(count) || count < 1) return;

    const message =
      count === 1 ? window.__t('cart_items_removed_one') : window.__t('cart_items_removed_other', {count});

    messageEl.textContent = message;
    banner.classList.add('rs-banner--visible');

    if (this._itemsRemovedBannerTimeout) {
      clearTimeout(this._itemsRemovedBannerTimeout);
    }

    this._itemsRemovedBannerTimeout = setTimeout(() => {
      const closeBtn = banner.querySelector('.rs-banner__close-button');
      if (closeBtn instanceof HTMLElement) {
        closeBtn.click();
      } else {
        this.hideItemsRemovedBanner();
      }
    }, 5000);
  }

  hideItemsRemovedBanner() {
    const banner = document.querySelector('.cart-page-actions__items-removed-banner');
    if (!banner) return;

    banner.classList.remove('rs-banner--visible');

    if (this._itemsRemovedBannerTimeout) {
      clearTimeout(this._itemsRemovedBannerTimeout);
      this._itemsRemovedBannerTimeout = null;
    }
  }

  showItemsRemovedBannerFromStorage() {
    const stored = sessionStorage.getItem(CART_ITEMS_REMOVED_STORAGE_KEY);
    sessionStorage.removeItem(CART_ITEMS_REMOVED_STORAGE_KEY);
    if (!stored) return;

    let count = 0;
    let timestamp = 0;
    try {
      const parsed = JSON.parse(stored);
      count = parseInt(parsed?.count, 10) || 0;
      timestamp = Number(parsed?.ts) || 0;
    } catch (e) {
      // Legacy plain-number value (no timestamp) — treat as not fresh.
      count = parseInt(stored, 10) || 0;
    }

    const isFresh = timestamp > 0 && Date.now() - timestamp <= CART_ITEMS_REMOVED_FRESHNESS_MS;
    if (count > 0 && isFresh) {
      this.showItemsRemovedBanner(count);
    }
  }

  /**
   * @param {string} url
   * @returns {boolean}
   */
  static isCartLineRemoveUrl(url) {
    if (!url) return false;
    try {
      const parsed = new URL(url, window.location.origin);
      return /\/cart\/change\/?$/.test(parsed.pathname) && parsed.searchParams.get('quantity') === '0';
    } catch (e) {
      return /cart\/change/.test(url) && /(?:\?|&)quantity=0(?:&|$)/.test(url);
    }
  }

  /**
   * Record a single line removal before navigating to Shopify's `/cart/change` remove URL.
   * @param {string} url
   */
  static notifyBeforeCartLineRemoveNavigate(url) {
    if (!CartPageActions.isCartLineRemoveUrl(url)) return;
    CartPageActions.setRemovedItemsCount(1);
  }

  /**
   * Persist removed item count (with a timestamp) so the banner can show after the cart reload that
   * immediately follows a removal — and only then.
   * @param {number} count
   */
  static setRemovedItemsCount(count) {
    if (!Number.isFinite(count) || count < 1) return;
    sessionStorage.setItem(
      CART_ITEMS_REMOVED_STORAGE_KEY,
      JSON.stringify({ count: count, ts: Date.now() })
    );
  }

  /**
   * Reset all cart attributes to empty strings.
   * Shopify keeps attribute keys; empty values clear them for subsequent checkouts.
   * @param {{ attributes?: Record<string, string> } | null} [cart]
   */
  static async clearCartAttributes(cart = null) {
    try {
      let attributes = cart?.attributes;
      if (!attributes) {
        const cartResponse = await fetch('/cart.js');
        const fetched = await cartResponse.json();
        attributes = fetched?.attributes || {};
      }

      const clearedAttributes = Object.fromEntries(
        Object.keys(attributes).map((key) => [key, ''])
      );
      if (Object.keys(clearedAttributes).length === 0) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: clearedAttributes }),
      });
    } catch (error) {
      console.error('Error clearing cart attributes:', error);
    }
  }

  /**
   * Clear cart line items and reset all cart attributes.
   * Shopify /cart/clear.js removes items but leaves attributes intact.
   */
  static async clearCartAndAttributes() {
    let cart = null;
    try {
      const cartResponse = await fetch('/cart.js');
      cart = await cartResponse.json();
    } catch (error) {
      console.error('Error fetching cart before clear:', error);
    }

    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await CartPageActions.clearCartAttributes(cart);
  }

  setupCartExport() {
    // Desktop
    const exportButton = this.querySelector('.cart-page-actions__export button');
    exportButton?.addEventListener('click', () => {
      this.openExportModal();
    });

    // Mobile
    const mobileExportButton = this.querySelector('.rs-hide-desktop .cart-page-actions__export');
    mobileExportButton?.addEventListener('click', () => {
      this.openExportModal();
    });

    const selectedExportButton = this.querySelector('#rs-cart-export-button');
    selectedExportButton?.addEventListener('click', () => {
      this.openExportModal();
    });

    // Bind once — calling this on every modal open stacked listeners and downloaded the CSV multiple times
    if (!this._exportCSVButtonBound) {
      this._exportCSVButtonBound = true;
      document.querySelector('.rs-export-csv-button')?.addEventListener('click', () => {
        this.createCartCSV();
      });
    }

    if (!this._exportModalCloseClickBound) {
      this._exportModalCloseClickBound = true;
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('.rs-export-modal .rs-modal-close')) return;
        this.closeExportModal();
      });
    }
  }

  /**
   * @param {HTMLElement} modal
   */
  activateExportModalFocusTrap(modal) {
    if (!(modal instanceof HTMLElement) || modal.classList.contains('rs-hidden')) return;

    const closeButton = modal.querySelector('.rs-modal__close-button');
    trapExportModalFocus(modal, {
      initialFocus: closeButton instanceof HTMLElement ? closeButton : undefined,
    });
  }

  /**
   * @param {HTMLElement} modal
   */
  scheduleExportModalFocusTrap(modal) {
    if (!(modal instanceof HTMLElement)) return;
    this.activateExportModalFocusTrap(modal);
  }

  openExportModal() {
    const exportModal = document.querySelector('.rs-export-modal');
    if (!(exportModal instanceof HTMLElement)) return;

    const activeEl = document.activeElement;
    this._exportModalTrigger = activeEl instanceof HTMLElement ? activeEl : null;

    exportModal.classList.remove('rs-hidden');
    exportModal.setAttribute('aria-hidden', 'false');
    exportModal.modalMask?.classList.remove('rs-hidden');
    this.scheduleExportModalFocusTrap(exportModal);

    // update the selected products label
    const cartTable = document.querySelector('.cart-table');
    const checkedItems = cartTable?.querySelectorAll('.rs-table-body__row--selected');
    const selectedProductsLabel = exportModal?.querySelector('[for="rs-export-selected-products"]');
    const cartItemsCount = cartTable?.querySelectorAll('.rs-table-body__row');
    if (selectedProductsLabel) {
      selectedProductsLabel.textContent = window.__t('selected_products', {count: checkedItems.length});
    }

    const selectedProductsRadio = exportModal?.querySelector('#rs-export-selected-products');
    const allProductsRadio = exportModal?.querySelector('#rs-export-all-products');
    if (checkedItems.length === 0) {
      selectedProductsRadio.disabled = true;
      selectedProductsRadio.checked = false;
      if (allProductsRadio) {
        allProductsRadio.checked = true;
      }
    } else {
      selectedProductsRadio.disabled = false;
    }

    if (cartItemsCount?.length === checkedItems?.length) {
      allProductsRadio.checked = true;
      selectedProductsRadio.checked = false;
    } else if (checkedItems?.length > 0) {
      allProductsRadio.checked = false;
      selectedProductsRadio.checked = true;
    }

    // Radio input events
    allProductsRadio?.addEventListener('change', () => {
      if (allProductsRadio?.checked) {
        selectedProductsRadio.checked = false;
      }
    });

    selectedProductsRadio?.addEventListener('change', () => {
      if (selectedProductsRadio?.checked) {
        allProductsRadio.checked = false;
      }
    });

    const exportWithPricesRadio = exportModal?.querySelector('#rs-export-csv-checkbox-with-prices');
    const exportWithoutPricesRadio = exportModal?.querySelector('#rs-export-csv-checkbox-without-prices');

    if (exportWithPricesRadio) {
      exportWithPricesRadio.checked = true;
    }

    if (exportWithoutPricesRadio) {
      exportWithoutPricesRadio.checked = false;
    }

    exportWithPricesRadio?.addEventListener('change', () => {
      if (exportWithPricesRadio?.checked) {
        exportWithoutPricesRadio.checked = false;
      }
    });

    exportWithoutPricesRadio?.addEventListener('change', () => {
      if (exportWithoutPricesRadio?.checked) {
        exportWithPricesRadio.checked = false;
      }
    });
  }

  closeExportModal() {
    releaseExportModalFocusTrap();

    const exportModal = document.querySelector('.rs-export-modal');
    if (exportModal) {
      exportModal.classList.add('rs-hidden');
      exportModal.setAttribute('aria-hidden', 'true');
      exportModal.modalMask?.classList.add('rs-hidden');
    }

    if (this._exportModalTrigger) {
      this._exportModalTrigger.focus({preventScroll: true});
      this._exportModalTrigger = null;
    }
  }

  escapeCsvField(value) {
    const stringValue = String(value ?? '').trim();
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  createCartCSV() {
    try {
      const cartTable = document.querySelector('.cart-table');
      if (!cartTable) return;

      let cartItems = null;
      // Check if there are items checked
      const checkedItems = cartTable.querySelectorAll('.rs-table-body__row--selected');
      const selectedProductsRadio = document.getElementById('rs-export-selected-products');
      if (checkedItems.length > 0 && selectedProductsRadio?.checked) {
        cartItems = checkedItems;
      } else {
        cartItems = cartTable.querySelectorAll('.cart-table__body .rs-table-body__row');
      }

      if (cartItems.length === 0) return;

      // Check if the export with prices checkbox is checked
      const exportWithoutPricesRadio = document.getElementById('rs-export-csv-checkbox-without-prices');
      let headers = '';
      let rows = '';
      if (exportWithoutPricesRadio?.checked) {
        headers = 'Customer Part Number,Quantity,Requested Delivery Date\n';
        // Create CSV rows
        rows = Array.from(cartItems)
          .map((item) => {
            const title = this.escapeCsvField(item?.querySelector('.cart-items__item-title')?.textContent || '');
            const requestedDeliveryDate = this.escapeCsvField(
              item?.querySelector('.cart-table__requested-delivery-date input')?.value || ''
            );
            const quantity = this.escapeCsvField(item?.querySelector('.rs-quantity-input')?.value || '');
            return `${title},${quantity},${requestedDeliveryDate}`;
          })
          .join('\n');
      } else {
        headers = 'Customer Part Number,Quantity,Requested Delivery Date, Price, Total\n';
        // Create CSV rows
        rows = Array.from(cartItems)
          .map((item) => {
            const title = this.escapeCsvField(item?.querySelector('.cart-items__item-title')?.textContent || '');
            const quantity = this.escapeCsvField(item?.querySelector('.rs-quantity-input')?.value || '');
            const requestedDeliveryDate = this.escapeCsvField(
              item?.querySelector('.cart-table__requested-delivery-date input')?.value || ''
            );
            const price = this.escapeCsvField(item?.querySelector('.cart-table__price-item')?.textContent || '');
            const lineTotal = this.escapeCsvField(
              item?.querySelector('.cart-table__price-total strong')?.textContent || ''
            );
            return `${title},${quantity},${requestedDeliveryDate},${price},${lineTotal}`;
          })
          .join('\n');
      }

      const csv = headers + rows;

      // Create download link
      const blob = new Blob([csv], {type: 'text/csv'});
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cart-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating cart CSV:', error);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const cartPageActions = document.querySelector('cart-page-actions');
  if (cartPageActions && cartPageActions instanceof CartPageActions) {
    // Restore previous state
    cartPageActions.restoreImageVisibilityState();
  }
});

// Define the custom element
customElements.define('cart-page-actions', CartPageActions);

window.setCartItemsRemovedCount = (count) => {
  CartPageActions.setRemovedItemsCount(count);
};

window.notifyCartLineRemovedBeforeNavigate = (url) => {
  CartPageActions.notifyBeforeCartLineRemoveNavigate(url);
};

window.clearCartAttributes = (cart) => {
  return CartPageActions.clearCartAttributes(cart);
};
